import { all, call, put, takeLatest } from 'redux-saga/effects';
import { AxiosCall, AxiosPost, AxiosPatch, AxiosDelete } from '../../../utils/axios';
import * as actions from './noteActions';

// Create Note
function* createNote(action) {
  try {
    const result = yield call(AxiosPost, '/mycore/note/add/', action.payload);
    yield put(actions.createNoteSuccess(result.data));
  } catch (e) {
    yield put(actions.createNoteFailure(e.response?.data?.error || e.message));
  }
}

// Update Note
function* updateNote(action) {
  try {
    const { noteId, data } = action.payload;
    const result = yield call(AxiosPatch, `/mycore/${noteId}/note/update/`, data);
    yield put(actions.updateNoteSuccess(result.data));
  } catch (e) {
    yield put(actions.updateNoteFailure(e.response?.data?.error || e.message));
  }
}

// Delete Note
function* deleteNote(action) {
  try {
    const noteId = action.payload;
    yield call(AxiosDelete, `/mycore/${noteId}/note/delete/`);
    yield put(actions.deleteNoteSuccess(noteId));
  } catch (e) {
    yield put(actions.deleteNoteFailure(e.response?.data?.error || e.message));
  }
}

// Get Notes by Organization (with optional filters)
function* getNotesByOrganization(action) {
  try {
    const { organizationId, batchId, params = {} } = action.payload;
    let url = `/mycore/note/list/?organization_id=${organizationId}`;

    // Add batch filter
    if (batchId) {
      url += `&batch_id=${batchId}`;
    }

    // Add additional filters from params
    if (params.search) {
      url += `&search=${encodeURIComponent(params.search)}`;
    }
    if (params.note_type) {
      url += `&note_type=${params.note_type}`;
    }
    if (params.tags) {
      url += `&tags=${encodeURIComponent(params.tags)}`;
    }
    if (params.start_date) {
      url += `&start_date=${params.start_date}`;
    }
    if (params.end_date) {
      url += `&end_date=${params.end_date}`;
    }
    if (params.is_pinned) {
      url += `&is_pinned=${params.is_pinned}`;
    }

    const result = yield call(AxiosCall, url);
    yield put(actions.getNotesByOrganizationSuccess(result.data));
  } catch (e) {
    yield put(actions.getNotesByOrganizationFailure(e.response?.data?.error || e.message));
  }
}

// Get Notes by Batch (backward compatibility - now uses organization endpoint)
function* getNotesByBatch(action) {
  try {
    const batchId = action.payload;
    const result = yield call(AxiosCall, `/mycore/note/list/?batch_id=${batchId}`);
    yield put(actions.getNotesByBatchSuccess(result.data));
  } catch (e) {
    yield put(actions.getNotesByBatchFailure(e.response?.data?.error || e.message));
  }
}

// Get Notes by Stage (legacy - may not be used, but kept for compatibility)
function* getNotesByStage(action) {
  try {
    const stageId = action.payload;
    // Note: Backend doesn't have stage-specific endpoint, would need organization_id
    // This is kept for compatibility but may need organization context
    const result = yield call(AxiosCall, `/mycore/note/list/?stage_id=${stageId}`);
    yield put(actions.getNotesByStageSuccess(result.data));
  } catch (e) {
    yield put(actions.getNotesByStageFailure(e.response?.data?.error || e.message));
  }
}

// Get Notes by Date Range (now requires organization_id)
function* getNotesByDate(action) {
  try {
    const { organizationId, batchId, startDate, endDate } = action.payload;
    let url = `/mycore/note/list/?organization_id=${organizationId}`;
    if (batchId) {
      url += `&batch_id=${batchId}`;
    }
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    const result = yield call(AxiosCall, url);
    yield put(actions.getNotesByDateSuccess(result.data));
  } catch (e) {
    yield put(actions.getNotesByDateFailure(e.response?.data?.error || e.message));
  }
}

// Get Notes by Tags (now requires organization_id)
function* getNotesByTags(action) {
  try {
    const { organizationId, batchId, tags } = action.payload;
    const tagsParam = Array.isArray(tags) ? tags.join(',') : tags;
    let url = `/mycore/note/list/?organization_id=${organizationId}&tags=${tagsParam}`;
    if (batchId) {
      url += `&batch_id=${batchId}`;
    }
    const result = yield call(AxiosCall, url);
    yield put(actions.getNotesByTagsSuccess(result.data));
  } catch (e) {
    yield put(actions.getNotesByTagsFailure(e.response?.data?.error || e.message));
  }
}

// Root Saga
export default function* noteSaga() {
  yield all([
    takeLatest(actions.CREATE_NOTE.REQUEST, createNote),
    takeLatest(actions.UPDATE_NOTE.REQUEST, updateNote),
    takeLatest(actions.DELETE_NOTE.REQUEST, deleteNote),
    takeLatest(actions.GET_NOTES_BY_ORGANIZATION.REQUEST, getNotesByOrganization),
    takeLatest(actions.GET_NOTES_BY_BATCH.REQUEST, getNotesByBatch),
    takeLatest(actions.GET_NOTES_BY_STAGE.REQUEST, getNotesByStage),
    takeLatest(actions.GET_NOTES_BY_DATE.REQUEST, getNotesByDate),
    takeLatest(actions.GET_NOTES_BY_TAGS.REQUEST, getNotesByTags),
  ]);
}
